import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Select, Spin, Segmented, DatePicker } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import useAuthStore from '../store/authStore';
import { getBranches } from '../api/branches';
import { getDailySchedule } from '../api/schedule';
import api from '../api/axios';
import { pickTermForDate } from '../utils/termForDate';
import SlotCard from '../components/calendar/SlotCard';
import SlotDetailModal from '../components/calendar/SlotDetailModal';
import EnrollStudentModal from '../components/calendar/EnrollStudentModal';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.locale('tr');

const ADMIN_ROLES = ['developer_admin', 'super_admin'];
const DAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const DEFAULT_DAY_START = 8 * 60 + 30;   // 08:30
const DEFAULT_DAY_END   = 22 * 60 + 30;  // 22:30
const SLOT_MIN   = 30;
const FULL_PX    = 64;
const MINI_PX    = 18;
const TIME_COL_W = 56;

const normalizeTime = (t) => (t || '').slice(0, 5);

const timeToMin = (t) => {
  const [h, m] = normalizeTime(t).split(':').map(Number);
  return h * 60 + m;
};

const buildHourLabels = (dayStart, dayEnd) => {
  const labels = [];
  for (let m = dayStart; m <= dayEnd; m += SLOT_MIN) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    labels.push({ label: `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`, mins: m });
  }
  return labels;
};

const buildTimeMap = (allSlots, dayStart, dayEnd) => {
  const occupied = new Set();
  (allSlots || []).forEach(slot => {
    const start = timeToMin(slot.start_time);
    const end   = timeToMin(slot.end_time) || start + 60;
    for (let m = start; m < end; m += SLOT_MIN) {
      if (m >= dayStart && m < dayEnd) occupied.add(m);
    }
  });

  const topY = {};
  let cumY = 0;
  for (let m = dayStart; m < dayEnd; m += SLOT_MIN) {
    topY[m] = cumY;
    cumY += occupied.has(m) ? FULL_PX : MINI_PX;
  }
  topY[dayEnd] = cumY;

  const timeToY = (mins) => {
    if (mins <= dayStart) return 0;
    if (mins >= dayEnd)   return topY[dayEnd];
    const slotStart = Math.floor((mins - dayStart) / SLOT_MIN) * SLOT_MIN + dayStart;
    const slotH = occupied.has(slotStart) ? FULL_PX : MINI_PX;
    const frac  = (mins - slotStart) / SLOT_MIN;
    return topY[slotStart] + frac * slotH;
  };

  return { timeToY, totalH: topY[dayEnd] + 8, topY, occupied };
};

const GridLines = ({ topY, occupied, dayStart, dayEnd, lessonStartMins }) => (
  <>
    {Array.from({ length: (dayEnd - dayStart) / SLOT_MIN }, (_, i) => {
      const m = dayStart + i * SLOT_MIN;
      const h = occupied.has(m) ? FULL_PX : MINI_PX;
      const isLessonStart = lessonStartMins?.has(m);
      return (
        <div
          key={m}
          style={{
            position: 'absolute',
            top: topY[m],
            left: 0,
            right: 0,
            height: h,
            borderBottom: isLessonStart ? '1px solid #e5e7eb' : 'none',
          }}
        />
      );
    })}
  </>
);

const Calendar = () => {
  const { user } = useAuthStore();
  const isAdmin   = ADMIN_ROLES.includes(user?.role);
  const isTeacher = user?.role === 'teacher';

  const [view, setView]               = useState('day');
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [branches, setBranches]       = useState([]);
  const [branchId, setBranchId]       = useState(null);
  const [slots, setSlots]             = useState({});
  const [loading, setLoading]         = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSlot, setDetailSlot] = useState(null);
  const [detailDate, setDetailDate] = useState(null);
  const [detailAutoMakeup, setDetailAutoMakeup] = useState(false);

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollSlot, setEnrollSlot] = useState(null);
  const [enrollDate, setEnrollDate] = useState(null);
  const [addLessonTime, setAddLessonTime] = useState(null);

  const [contextMenu, setContextMenu] = useState(null);
  const contextRef = useRef(null);

  const [dayStart, setDayStart] = useState(DEFAULT_DAY_START);
  const [dayEnd, setDayEnd]     = useState(DEFAULT_DAY_END);

  const hourLabels = useMemo(() => buildHourLabels(dayStart, dayEnd), [dayStart, dayEnd]);

  useEffect(() => {
    if (isAdmin) {
      getBranches().then(res => {
        const list = res.data?.data || res.data || [];
        setBranches(list);
        if (list.length > 0) setBranchId(list[0].id);
      }).catch(() => {});
    } else {
      setBranchId(user?.branch_id || null);
    }
  }, []);

  // Takvimin görünür saat aralığı. Eskiden `per_page: 1` ile tek dönem
  // çekilip onun saatleri kullanılıyordu; /terms `start_date desc` sıraladığı
  // için bu her zaman EN GEÇ BAŞLAYAN dönemdi. İki aktif dönemin saatleri
  // farklıysa (yeni dönem 14:30–20:30, eski 08:30–22:30) takvim bakılan
  // tarihten bağımsız olarak yanlış aralığı gösteriyordu.
  //
  // Artık BAKILAN TARİHİ kapsayan döneme bakılıyor. Dönemde gün bazlı ayar
  // varsa (session_config, toplu ders üretimi formundan) o günün gerçek
  // aralığı kullanılır; hafta görünümünde görünen günlerin birleşimi alınır.
  // Kapsayan dönem ya da ayar yoksa eski davranışa düşülür.
  useEffect(() => {
    const bid = branchId || user?.branch_id;
    if (!bid) return;

    const gorunenler = view === 'week'
      ? Array.from({ length: 7 }, (_, i) => currentDate.startOf('isoWeek').add(i, 'day'))
      : [currentDate];

    api.get('/terms', { params: { branch_id: bid, is_active: 1, per_page: 100 } })
      .then(res => {
        const terms = res.data?.data || res.data || [];
        const term = pickTermForDate(terms, currentDate.format('YYYY-MM-DD'));

        if (!term) {
          setDayStart(DEFAULT_DAY_START);
          setDayEnd(DEFAULT_DAY_END);
          return;
        }

        const gunAyarlari = term.session_config?.days;
        if (gunAyarlari) {
          const baslangiclar = [];
          const bitisler = [];
          gorunenler.forEach(d => {
            const ayar = gunAyarlari[d.day()]; // dayjs .day(): 0=Pazar — backend ile aynı
            if (ayar?.start && ayar?.end) {
              baslangiclar.push(timeToMin(ayar.start));
              bitisler.push(timeToMin(ayar.end));
            }
          });
          if (baslangiclar.length) {
            setDayStart(Math.min(...baslangiclar));
            setDayEnd(Math.max(...bitisler));
            return;
          }
        }

        if (term.first_slot_time && term.last_slot_time) {
          setDayStart(timeToMin(term.first_slot_time));
          setDayEnd(timeToMin(term.last_slot_time));
        } else {
          setDayStart(DEFAULT_DAY_START);
          setDayEnd(DEFAULT_DAY_END);
        }
      })
      .catch(() => {
        setDayStart(DEFAULT_DAY_START);
        setDayEnd(DEFAULT_DAY_END);
      });
  }, [branchId, view, currentDate]);

  const getDatesToLoad = useCallback(() => {
    if (view === 'day') return [currentDate.format('YYYY-MM-DD')];
    if (view === 'week') {
      const start = currentDate.startOf('isoWeek');
      return Array.from({ length: 7 }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
    }
    return [];
  }, [view, currentDate]);

  const fetchSchedule = useCallback(async () => {
    if (!branchId && !user?.branch_id) return;
    setLoading(true);
    const dates = getDatesToLoad();
    try {
      const results = await Promise.all(
        dates.map(date =>
          getDailySchedule({ date, branch_id: branchId || user?.branch_id, scope: isTeacher ? 'branch' : undefined })
            .then(res => ({ date, data: res.data }))
            .catch(() => ({ date, data: [] }))
        )
      );
      const map = {};
      results.forEach(({ date, data }) => { map[date] = data || []; });
      setSlots(map);
    } finally {
      setLoading(false);
    }
  }, [branchId, getDatesToLoad, user?.branch_id]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  useEffect(() => {
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const navigate = (dir) => {
    const unit = view === 'day' ? 'day' : 'week';
    setCurrentDate(prev => prev.add(dir, unit));
  };

  const openDetail = (slot, date, autoMakeup = false) => {
    setDetailSlot({ ...slot, start_time: normalizeTime(slot.start_time) });
    setDetailDate(date);
    setDetailAutoMakeup(autoMakeup);
    setDetailOpen(true);
    setContextMenu(null);
  };

  const openEnroll = (slot, date) => {
    setEnrollSlot({ ...slot, start_time: normalizeTime(slot.start_time), end_time: normalizeTime(slot.end_time) });
    setEnrollDate(date);
    setEnrollOpen(true);
    setContextMenu(null);
  };

  const openAddLesson = (date, time) => {
    setEnrollSlot(null);
    setEnrollDate(date);
    setEnrollOpen(true);
    setContextMenu(null);
    // Store initialTime for the modal via a ref-like state
    setAddLessonTime(time);
  };

  const handleContextMenu = (e, slot, date) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, slot, date });
  };

  const handleGridContextMenu = (e, date, topY) => {
    e.preventDefault();
    if (isTeacher) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let best = dayStart;
    for (let m = dayStart; m < dayEnd; m += SLOT_MIN) {
      if ((topY[m] ?? 0) <= y) best = m;
      else break;
    }
    const time = `${String(Math.floor(best / 60)).padStart(2, '0')}:${String(best % 60).padStart(2, '0')}`;
    setContextMenu({ x: e.clientX, y: e.clientY, slot: null, date, time });
  };

  const titleLabel = () => {
    if (view === 'day') return currentDate.format('DD MMMM YYYY dddd');
    if (view === 'week') {
      const start = currentDate.startOf('isoWeek');
      const end   = currentDate.endOf('isoWeek');
      return `${start.format('DD MMM')} – ${end.format('DD MMM YYYY')}`;
    }
    return currentDate.format('MMMM YYYY');
  };

  // Ders kartları — timeToY prop ile
  const GridContent = ({ date, compact = false, timeToY }) => {
    const daySlots = slots[date] || [];
    const timeGroups = {};
    daySlots.forEach(slot => {
      const key = normalizeTime(slot.start_time);
      if (!timeGroups[key]) timeGroups[key] = [];
      timeGroups[key].push(slot);
    });

    return (
      <>
        {daySlots.map((slot, idx) => {
          const startMin = timeToMin(slot.start_time);
          const endMin   = timeToMin(slot.end_time) || startMin + 60;
          const y        = timeToY(startMin);
          const h        = Math.max(timeToY(endMin) - y - 4, 36);

          const sameTime = timeGroups[normalizeTime(slot.start_time)] || [];
          const colIdx   = sameTime.indexOf(slot);
          const colCount = sameTime.length;
          const w        = `calc(${100 / Math.max(colCount, 1)}% - 6px)`;
          const left     = `calc(${(colIdx / Math.max(colCount, 1)) * 100}% + 3px)`;

          return (
            <div key={idx} style={{ position: 'absolute', top: y + 2, left, width: w, height: h, zIndex: 1 }}>
              <SlotCard
                slot={slot}
                compact={compact}
                showTime={!compact}
                isPast={!dayjs(date).isAfter(dayjs(), 'day')}
                onClick={() => openDetail(slot, date)}
                onContextMenu={(e) => handleContextMenu(e, slot, date)}
              />
            </div>
          );
        })}
      </>
    );
  };

  // --- ORTAK YATAY GÖRÜNÜM SABİTLERİ ---
  const SLOT_W       = 110; // px per 30-min column
  const DAY_LW       = 58; // px for day label column
  const WEEK_TRACK_H = 120; // px per track in weekly view

  const assignTracks = (daySlots) => {
    const trackEnds = [];
    return daySlots.map(slot => {
      const start = timeToMin(slot.start_time);
      const end   = timeToMin(slot.end_time) || start + 60;
      let track = trackEnds.findIndex(e => e <= start);
      if (track === -1) { track = trackEnds.length; trackEnds.push(end); }
      else trackEnds[track] = end;
      return { slot, track, start, end };
    });
  };

  const HorizontalTimeHeader = ({ totalW, minToX, hourLabels: labels }) => (
    <div className="flex sticky top-0 bg-white border-b" style={{ zIndex: 20 }}>
      <div style={{ width: DAY_LW, minWidth: DAY_LW, flexShrink: 0 }} />
      <div className="relative" style={{ width: totalW, height: 28, flexShrink: 0 }}>
        {(labels ?? hourLabels).map(({ label, mins }) => (
          <div
            key={label}
            style={{
              position: 'absolute',
              left: minToX(mins),
              width: SLOT_W * 2,
              fontSize: 10,
              color: '#9ca3af',
              paddingTop: 7,
              paddingLeft: 3,
              userSelect: 'none',
            }}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );

  const HorizontalGridLines = ({ totalSlots, lessonStartMins }) => (
    <>
      {Array.from({ length: totalSlots }, (_, si) => {
        const m = dayStart + si * SLOT_MIN;
        if (!lessonStartMins?.has(m)) return null;
        return (
          <div key={si} style={{
            position: 'absolute', top: 0, bottom: 0,
            left: si * SLOT_W, width: 1,
            background: '#e5e7eb',
          }} />
        );
      })}
    </>
  );

  // --- GÜNLÜK GÖRÜNÜM (dikey: saatler solda) ---
  const DayView = ({ date }) => {
    const daySlots = slots[date] || [];
    const { timeToY, totalH, topY, occupied } = buildTimeMap(daySlots, dayStart, dayEnd);
    const lessonStartMins = new Set(daySlots.map(s => timeToMin(s.start_time)));

    return (
      <div className="flex">
        {/* Saat sütunu */}
        <div style={{ width: TIME_COL_W, minWidth: TIME_COL_W, position: 'relative', height: totalH, flexShrink: 0 }}>
          {hourLabels.filter(({ mins }) => lessonStartMins.has(mins)).map(({ label, mins }) => (
            <div
              key={label}
              style={{ position: 'absolute', top: timeToY(mins) - 8, right: 8, fontSize: 11, color: '#9ca3af', userSelect: 'none' }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* İçerik alanı */}
        <div
          className="flex-1 relative"
          style={{ height: totalH }}
          onContextMenu={(e) => handleGridContextMenu(e, date, topY)}
        >
          <GridLines topY={topY} occupied={occupied} dayStart={dayStart} dayEnd={dayEnd} lessonStartMins={lessonStartMins} />
          <GridContent date={date} timeToY={timeToY} />
        </div>
      </div>
    );
  };

  const WeekView = () => {
    const weekDates = Array.from({ length: 7 }, (_, i) =>
      currentDate.startOf('isoWeek').add(i, 'day').format('YYYY-MM-DD')
    );
    const todayStr  = dayjs().format('YYYY-MM-DD');
    const totalSlots = (dayEnd - dayStart) / SLOT_MIN;
    const totalW     = totalSlots * SLOT_W;
    const minToX     = (mins) => ((mins - dayStart) / SLOT_MIN) * SLOT_W;

    // Pseudo topY for empty-grid right-click (flat mapping)
    const flatTopY = {};
    for (let m = dayStart; m < dayEnd; m += SLOT_MIN) flatTopY[m] = (m - dayStart) / SLOT_MIN * SLOT_W;

    const weekLessonStartMins = new Set(
      weekDates.flatMap(d => (slots[d] || []).map(s => timeToMin(s.start_time)))
    );
    const filteredHourLabels = hourLabels.filter(({ mins }) => weekLessonStartMins.has(mins));

    return (
      <div className="overflow-x-auto">
        <div style={{ minWidth: DAY_LW + totalW }}>

          <HorizontalTimeHeader totalW={totalW} minToX={minToX} hourLabels={filteredHourLabels} />

          {/* Gün satırları */}
          {weekDates.map((d, i) => {
            const daySlots  = slots[d] || [];
            const tracked   = assignTracks(daySlots);
            const numTracks = Math.max(1, ...tracked.map(t => t.track + 1));
            const rowH      = numTracks * WEEK_TRACK_H;
            const isToday   = d === todayStr;

            return (
              <div key={d} className="flex border-b" style={{ minHeight: rowH + 8 }}>
                {/* Tarih etiketi */}
                <div
                  style={{
                    width: DAY_LW, minWidth: DAY_LW, flexShrink: 0,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: isToday ? '#f0f7ff' : undefined,
                    borderRight: '1px solid #e5e7eb',
                    cursor: 'pointer',
                  }}
                  onClick={() => { setCurrentDate(dayjs(d)); setView('day'); }}
                >
                  <span style={{ fontSize: 10, color: isToday ? '#1677ff' : '#6b7280', fontWeight: 600 }}>
                    {DAYS_TR[i]}
                  </span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: isToday ? '#1677ff' : '#111827', lineHeight: 1.2 }}>
                    {dayjs(d).format('DD')}
                  </span>
                </div>

                {/* İçerik alanı */}
                <div
                  className="relative"
                  style={{ width: totalW, height: rowH + 8 }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (isTeacher) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const slotIdx = Math.floor(x / SLOT_W);
                    const mins = dayStart + slotIdx * SLOT_MIN;
                    const clamped = Math.max(dayStart, Math.min(dayEnd - SLOT_MIN, mins));
                    const time = `${String(Math.floor(clamped/60)).padStart(2,'0')}:${String(clamped%60).padStart(2,'0')}`;
                    setContextMenu({ x: e.clientX, y: e.clientY, slot: null, date: d, time });
                  }}
                >
                  <HorizontalGridLines totalSlots={totalSlots} lessonStartMins={weekLessonStartMins} />

                  {tracked.map(({ slot, track, start, end }, idx) => {
                    const x = minToX(start);
                    const w = Math.max(minToX(end) - x - 4, 36);
                    const y = track * WEEK_TRACK_H + 4;
                    return (
                      <div key={idx} style={{ position: 'absolute', left: x + 2, top: y, width: w, height: WEEK_TRACK_H - 8, zIndex: 1 }}>
                        <SlotCard
                          slot={slot}
                          compact={false}
                          showTime
                          isPast={!dayjs(d).isAfter(dayjs(), 'day')}
                          onClick={() => openDetail(slot, d)}
                          onContextMenu={(e) => { e.stopPropagation(); handleContextMenu(e, slot, d); }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="small" onClick={() => setCurrentDate(dayjs())}>Bugün</Button>
          <Button icon={<LeftOutlined />} size="small" onClick={() => navigate(-1)} />
          <Button icon={<RightOutlined />} size="small" onClick={() => navigate(1)} />
          <DatePicker
            value={currentDate}
            onChange={(date) => date && setCurrentDate(date)}
            format={view === 'day' ? 'DD MMMM YYYY dddd' : '[Hafta] W, MMMM YYYY'}
            picker={view === 'week' ? 'week' : 'date'}
            allowClear={false}
            size="small"
            style={{ fontWeight: 600 }}
          />
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Select
              style={{ width: 160 }}
              placeholder="Şube"
              value={branchId}
              onChange={setBranchId}
              options={branches.map(b => ({ label: b.name, value: b.id }))}
            />
          )}
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: 'Günlük', value: 'day' },
              { label: 'Haftalık', value: 'week' },
            ]}
          />
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border p-4 relative overflow-auto" style={{ maxHeight: '80vh' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl z-20" style={{ background: 'rgba(255,255,255,0.75)' }}>
            <Spin size="large" />
          </div>
        )}
        {view === 'day'  && <DayView date={currentDate.format('YYYY-MM-DD')} />}
        {view === 'week' && <WeekView />}
      </div>

      {/* Sağ tık menüsü */}
      {contextMenu && (
        <div
          ref={contextRef}
          className="fixed z-50 bg-white border rounded-lg shadow-lg py-1"
          style={{ top: contextMenu.y, left: contextMenu.x, minWidth: 180 }}
          onClick={e => e.stopPropagation()}
        >
          {contextMenu.slot ? (
            <>
              <div className="px-3 py-1 text-xs text-gray-400 border-b mb-1">
                {normalizeTime(contextMenu.slot.start_time)} · {contextMenu.slot.classroom_name}
              </div>
              <div className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm" onClick={() => openDetail(contextMenu.slot, contextMenu.date)}>
                Detayı Görüntüle
              </div>
              <div className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm" onClick={() => openEnroll(contextMenu.slot, contextMenu.date)}>
                Öğrenci Ekle
              </div>
              {isTeacher && (
                <div className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-orange-600" onClick={() => openDetail(contextMenu.slot, contextMenu.date, true)}>
                  Telafi Öğrencisi Ekle
                </div>
              )}
            </>
          ) : !isTeacher ? (
            <>
              <div className="px-3 py-1 text-xs text-gray-400 border-b mb-1">
                {contextMenu.date} · {contextMenu.time}
              </div>
              <div className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm font-medium text-blue-600" onClick={() => openAddLesson(contextMenu.date, contextMenu.time)}>
                + Ders Ekle
              </div>
            </>
          ) : null}
        </div>
      )}

      <SlotDetailModal
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailAutoMakeup(false); }}
        onDelete={() => { setDetailOpen(false); setDetailAutoMakeup(false); fetchSchedule(); }}
        // Modal içinde doluluk değişince (öğrenci ekleme, telafi ekleme,
        // "gelmeyecek" işaretleme) ızgaradaki sayı da tazelensin - modal
        // açık kalmaya devam ediyor, sadece arkadaki veri güncelleniyor.
        onChanged={fetchSchedule}
        date={detailDate}
        classroomId={detailSlot?.classroom_id}
        startTime={detailSlot?.start_time}
        classroomName={detailSlot?.classroom_name}
        autoOpenMakeup={detailAutoMakeup}
      />

      <EnrollStudentModal
        open={enrollOpen}
        onClose={() => { setEnrollOpen(false); setAddLessonTime(null); }}
        onSuccess={fetchSchedule}
        slot={enrollSlot}
        date={enrollDate}
        branchId={branchId || user?.branch_id}
        initialTime={addLessonTime}
      />
    </div>
  );
};

export default Calendar;
